import type { IntegrationState, Project, ProjectListItem } from '../../api/projects';

/**
 * Los filtros y el orden de la tabla de proyectos, fuera de la pantalla para
 * poder probarlos sin montarla —y para que el fichero de la pantalla exporte
 * solo componentes, que es lo que pide el refresco en caliente—.
 */

export const STATUSES = ['planning', 'active', 'paused', 'archived'] as const;

export type StatusFilter = '' | Project['status'];
export type IntegrationFilter = '' | IntegrationState;

export interface Filters {
  query: string;
  status: StatusFilter;
  kubernetes: IntegrationFilter;
  jenkins: IntegrationFilter;
}

export const NO_FILTERS: Filters = { query: '', status: '', kubernetes: '', jenkins: '' };

export type SortKey = 'name' | 'status' | 'tasks' | 'lastRequested' | 'lastFinished';

export interface Sort {
  key: SortKey;
  direction: 'asc' | 'desc';
}

/** Por defecto, el proyecto con movimiento más reciente arriba. */
export const DEFAULT_SORT: Sort = { key: 'lastRequested', direction: 'desc' };

/** Nombre, slug o cualquier elemento de la pila, sin distinguir mayúsculas. */
export function matches(project: ProjectListItem, filters: Filters): boolean {
  const needle = filters.query.trim().toLocaleLowerCase();
  const haystack = [project.name, project.slug, ...project.tech_stack].join(' ').toLocaleLowerCase();

  return (
    (needle === '' || haystack.includes(needle)) &&
    (filters.status === '' || project.status === filters.status) &&
    (filters.kubernetes === '' || project.summary.kubernetes.state === filters.kubernetes) &&
    (filters.jenkins === '' || project.summary.jenkins.state === filters.jenkins)
  );
}

/** Una fecha ausente va siempre al final, se ordene como se ordene. */
export function sortProjects(rows: ProjectListItem[], sort: Sort): ProjectListItem[] {
  const sign = sort.direction === 'asc' ? 1 : -1;
  const byMoment = (a: string | null, b: string | null): number =>
    a === null && b === null ? 0 : a === null ? 1 : b === null ? -1 : sign * a.localeCompare(b);

  return [...rows].sort((a, b) => {
    switch (sort.key) {
      case 'name':
        return sign * a.name.localeCompare(b.name);
      case 'status':
        return sign * (STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status));
      case 'tasks':
        return sign * (a.summary.tasks.open - b.summary.tasks.open) || sign * (a.summary.tasks.total - b.summary.tasks.total);
      case 'lastRequested':
        return byMoment(a.summary.tasks.last_requested_at, b.summary.tasks.last_requested_at);
      case 'lastFinished':
        return byMoment(a.summary.tasks.last_finished_at, b.summary.tasks.last_finished_at);
    }
  });
}
